import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Search, Plus, Pencil, Trash2, Mail, Eye, ExternalLink, Link2, GripVertical,
  FileText, Table2, Presentation, ClipboardList, HardDrive, File, Globe, Loader2, X, Printer, Settings2,
} from 'lucide-react';

/**
 * Library — the org's program documents as quick links (docs/LIBRARY.md, AJ 2026-10-19).
 *
 * Coloured section cards (masonry columns) → one compact row per document with a
 * type badge (Docs / Sheets / Slides / Forms / Drive / PDF / link). Search filters
 * title, description, tags and section as you type ("/" focuses it). Each row:
 * Preview (Google's own preview in a panel) · Open (new tab — print from there) ·
 * Email (sends the link) · Copy link · Edit · Remove. "Arrange" turns on drag
 * handles to reorder sections and move / reorder documents between sections.
 * The file itself never lives in OL — removing a document only removes the link.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#db2777', '#65a30d', '#4f46e5', '#ea580c'];
const KIND = {
  doc:    { label: 'Docs',   color: '#4285F4', Icon: FileText },
  sheet:  { label: 'Sheets', color: '#0F9D58', Icon: Table2 },
  slides: { label: 'Slides', color: '#F4B400', Icon: Presentation },
  form:   { label: 'Forms',  color: '#7248B9', Icon: ClipboardList },
  drive:  { label: 'Drive',  color: '#1E88E5', Icon: HardDrive },
  pdf:    { label: 'PDF',    color: '#DB4437', Icon: File },
  link:   { label: 'Link',   color: '#64748B', Icon: Globe },
};
// Starter sections, offered once on an empty library (renamable, deletable).
const STARTER = ['Registration & Forms', 'Policies & Handbooks', 'Coaches', 'Teams & Rosters', 'Events & Calendars', 'Finance'];

const googleId = (url) => (url.match(/\/d\/([a-zA-Z0-9_-]+)/) || [])[1];
/** Google's own preview for a Google link; otherwise the link itself (may refuse to frame). */
export function previewUrl(url, kind) {
  const id = googleId(url || '');
  if (kind === 'doc' && id) return `https://docs.google.com/document/d/${id}/preview`;
  if (kind === 'sheet' && id) return `https://docs.google.com/spreadsheets/d/${id}/preview`;
  if (kind === 'slides' && id) return `https://docs.google.com/presentation/d/${id}/preview`;
  if (kind === 'drive' && id) return `https://drive.google.com/file/d/${id}/preview`;
  if (kind === 'form') return url.includes('?') ? `${url}&embedded=true` : `${url}?embedded=true`;
  return url;
}

const sectionColor = (s, i) => s.color || PALETTE[i % PALETTE.length];

function Highlight({ text, q }) {
  if (!q || !text) return text || null;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark className="bg-yellow-200 dark:bg-yellow-600/60 text-inherit rounded px-0.5">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-auto" onClick={onClose}>
      <div className={`w-full ${wide ? 'max-w-5xl' : 'max-w-md'} mt-8 rounded-xl border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-2xl`}
        onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white truncate">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const field = 'w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-2.5 py-1.5 text-sm';

export default function LibraryPanel({ orgId }) {
  const [sections, setSections] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [only, setOnly] = useState('');            // section filter chip
  const [arrange, setArrange] = useState(false);
  const [docForm, setDocForm] = useState(null);    // {} new | doc
  const [secForm, setSecForm] = useState(null);    // {} new | section
  const [preview, setPreview] = useState(null);    // doc
  const [emailDoc, setEmailDoc] = useState(null);  // doc
  const searchRef = useRef(null);
  const drag = useRef(null);
  const base = `${API_URL}/api/library/${orgId}`;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(base);
      setSections(data.sections || []);
      setDocs(data.docs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the Library');
    } finally { setLoading(false); }
  }, [base]);
  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  useEffect(() => {
    const h = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (e.key === '/' && !['input', 'textarea', 'select'].includes(tag)) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const run = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); await load(); return true; }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); return false; }
  };

  const ql = q.trim().toLowerCase();
  const view = useMemo(() => sections.map((s, i) => {
    const mine = docs.filter((d) => d.section_id === s.id);
    const hit = (d) => !ql || [d.title, d.description, s.name, ...(d.tags || [])].some((t) => (t || '').toLowerCase().includes(ql));
    return { s, color: sectionColor(s, i), docs: mine.filter(hit), total: mine.length };
  }).filter((v) => (!only || v.s.id === only) && (!ql || v.docs.length || v.s.name.toLowerCase().includes(ql))), [sections, docs, ql, only]);
  const hits = view.reduce((n, v) => n + v.docs.length, 0);

  // ── drag & drop (Arrange) ─────────────────────────────────────────────────
  const dropDoc = (sectionId, beforeId) => async (e) => {
    e.preventDefault(); e.stopPropagation();
    const d = drag.current; drag.current = null;
    if (!d || d.type !== 'doc') return;
    const ids = docs.filter((x) => x.section_id === sectionId && x.id !== d.id).map((x) => x.id);
    const at = beforeId ? ids.indexOf(beforeId) : ids.length;
    ids.splice(at < 0 ? ids.length : at, 0, d.id);
    await run(() => axios.post(`${base}/docs/reorder`, { section_id: sectionId, ids }));
  };
  const dropSection = (beforeId) => async (e) => {
    e.preventDefault();
    const d = drag.current; drag.current = null;
    if (!d || d.type !== 'section' || d.id === beforeId) return;
    const ids = sections.map((s) => s.id).filter((id) => id !== d.id);
    ids.splice(ids.indexOf(beforeId), 0, d.id);
    await run(() => axios.post(`${base}/sections/reorder`, { ids }));
  };

  const copy = async (d) => {
    try { await navigator.clipboard.writeText(d.url); toast.success('Link copied'); }
    catch { toast.error('Could not copy — open the document and copy its link'); }
  };
  const remove = (d) => {
    if (!window.confirm(`Remove "${d.title}" from the Library?\n\nOnly the link is removed — the document itself is not touched.`)) return;
    run(() => axios.delete(`${base}/docs/${d.id}`), 'Removed from the Library');
  };
  const starter = () => run(async () => {
    for (let i = 0; i < STARTER.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await axios.post(`${base}/sections`, { name: STARTER[i], color: PALETTE[i % PALETTE.length] });
    }
  }, 'Sections added — rename or remove any of them');

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="library-panel">
      {/* Header: title, search, actions */}
      <div className="rounded-xl p-4 text-white shadow-sm"
        style={{ background: 'linear-gradient(120deg, #1e3a8a 0%, #7c3aed 55%, #db2777 100%)' }}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-auto">
            <div className="p-2 rounded-lg bg-white/15"><BookOpen className="h-5 w-5" /></div>
            <div>
              <h2 className="text-lg font-bold leading-tight">Library</h2>
              <p className="text-xs text-white/80">{docs.length} documents · {sections.length} sections</p>
            </div>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="library-search"
              placeholder="Search documents, tags, sections…   ( / )"
              className="w-full rounded-lg bg-white text-slate-900 pl-8 pr-8 py-2 text-sm shadow-inner outline-none focus:ring-2 focus:ring-yellow-300" />
            {q && <button type="button" onClick={() => setQ('')} className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setDocForm({ section_id: only || sections[0]?.id || '' })} disabled={!sections.length}
              data-testid="library-add-doc"
              className="inline-flex items-center gap-1 rounded-lg bg-yellow-300 text-slate-900 font-semibold text-sm px-3 py-2 hover:bg-yellow-200 disabled:opacity-50">
              <Plus className="h-4 w-4" /> Document</button>
            <button type="button" onClick={() => setSecForm({})} data-testid="library-add-section"
              className="inline-flex items-center gap-1 rounded-lg bg-white/15 hover:bg-white/25 text-sm px-3 py-2">
              <Plus className="h-4 w-4" /> Section</button>
            <button type="button" onClick={() => setArrange((v) => !v)} data-testid="library-arrange"
              className={`inline-flex items-center gap-1 rounded-lg text-sm px-3 py-2 ${arrange ? 'bg-white text-slate-900' : 'bg-white/15 hover:bg-white/25'}`}>
              <Settings2 className="h-4 w-4" /> {arrange ? 'Done arranging' : 'Arrange'}</button>
          </div>
        </div>
        {sections.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            <button type="button" onClick={() => setOnly('')}
              className={`text-xs rounded-full px-2.5 py-1 ${!only ? 'bg-white text-slate-900 font-semibold' : 'bg-white/15 hover:bg-white/25'}`}>All</button>
            {sections.map((s, i) => (
              <button key={s.id} type="button" onClick={() => setOnly(only === s.id ? '' : s.id)}
                className={`text-xs rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 ${only === s.id ? 'bg-white text-slate-900 font-semibold' : 'bg-white/15 hover:bg-white/25'}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: sectionColor(s, i) }} />{s.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {ql && <div className="text-xs text-slate-500" data-testid="library-results">{hits} result{hits !== 1 ? 's' : ''} for “{q}”</div>}
      {arrange && <div className="text-xs rounded-md px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
        Drag a section by its <GripVertical className="inline h-3 w-3" /> to reorder sections; drag a document onto another document or section to move it.</div>}

      {sections.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 p-10 text-center space-y-3">
          <BookOpen className="h-10 w-10 mx-auto text-violet-500" />
          <p className="text-slate-600 dark:text-slate-300">Your Library is empty. Add a section, then add your documents' links.</p>
          <div className="flex justify-center gap-2">
            <button type="button" onClick={starter} data-testid="library-starter"
              className="rounded-lg bg-violet-600 text-white text-sm px-3 py-2 hover:bg-violet-700">Start with suggested sections</button>
            <button type="button" onClick={() => setSecForm({})} className="rounded-lg border text-sm px-3 py-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200">Add a section</button>
          </div>
        </div>
      )}

      {/* Section cards in masonry columns */}
      <div className="columns-1 md:columns-2 2xl:columns-3 gap-4 [column-fill:_balance]">
        {view.map(({ s, color, docs: list, total }) => (
          <div key={s.id} className="break-inside-avoid mb-4 rounded-xl overflow-hidden border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm"
            data-testid={`library-section-${s.id}`}
            draggable={arrange} onDragStart={() => { drag.current = { type: 'section', id: s.id }; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => (drag.current?.type === 'section' ? dropSection(s.id)(e) : dropDoc(s.id, null)(e))}>
            <div className="flex items-center gap-2 px-3 py-2 text-white" style={{ background: color }}>
              {arrange && <GripVertical className="h-4 w-4 opacity-80 cursor-grab" />}
              <span className="font-semibold truncate">{s.name}</span>
              <span className="text-xs rounded-full bg-white/25 px-2 py-0.5">{total}</span>
              <span className="flex-1" />
              <button type="button" title="Add a document here" onClick={() => setDocForm({ section_id: s.id })}
                className="p-1 rounded hover:bg-white/20"><Plus className="h-4 w-4" /></button>
              <button type="button" title="Rename / colour / delete" onClick={() => setSecForm(s)} data-testid={`library-edit-section-${s.id}`}
                className="p-1 rounded hover:bg-white/20"><Pencil className="h-3.5 w-3.5" /></button>
            </div>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.length === 0 && <li className="px-3 py-3 text-xs text-slate-400">{ql ? 'No matches here' : 'No documents yet'}</li>}
              {list.map((d) => {
                const k = KIND[d.kind] || KIND.link;
                return (
                  <li key={d.id} className="group flex items-start gap-2.5 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    data-testid={`library-doc-${d.id}`}
                    draggable={arrange} onDragStart={(e) => { e.stopPropagation(); drag.current = { type: 'doc', id: d.id }; }}
                    onDragOver={(e) => e.preventDefault()} onDrop={dropDoc(s.id, d.id)}>
                    {arrange && <GripVertical className="h-4 w-4 mt-1 text-slate-400 cursor-grab shrink-0" />}
                    <span className="mt-0.5 shrink-0 rounded-md p-1.5" style={{ background: `${k.color}1f`, color: k.color }} title={k.label}>
                      <k.Icon className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <button type="button" onClick={() => setPreview(d)}
                        className="text-left text-sm font-medium text-slate-900 dark:text-slate-100 hover:underline break-words">
                        <Highlight text={d.title} q={ql} />
                      </button>
                      {d.description && <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2"><Highlight text={d.description} q={ql} /></div>}
                      {(d.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">{d.tags.map((t) => (
                          <button key={t} type="button" onClick={() => setQ(t)}
                            className="text-[10px] rounded-full px-1.5 py-0.5 border" style={{ borderColor: `${color}66`, color }}>#{t}</button>
                        ))}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0 opacity-60 group-hover:opacity-100">
                      <IconBtn title="Preview" onClick={() => setPreview(d)} testid={`library-preview-${d.id}`}><Eye /></IconBtn>
                      <IconBtn title="Open (print from there)" href={d.url}><ExternalLink /></IconBtn>
                      <IconBtn title="Email this document" onClick={() => setEmailDoc(d)} testid={`library-email-${d.id}`}><Mail /></IconBtn>
                      <IconBtn title="Copy link" onClick={() => copy(d)}><Link2 /></IconBtn>
                      <IconBtn title="Edit" onClick={() => setDocForm(d)} testid={`library-editdoc-${d.id}`}><Pencil /></IconBtn>
                      <IconBtn title="Remove from the Library" onClick={() => remove(d)} danger testid={`library-remove-${d.id}`}><Trash2 /></IconBtn>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {docForm && <DocDialog doc={docForm} sections={sections} onClose={() => setDocForm(null)}
        onSave={async (body) => {
          const ok = await run(() => (docForm.id ? axios.put(`${base}/docs/${docForm.id}`, body) : axios.post(`${base}/docs`, body)),
            docForm.id ? 'Document updated' : 'Document added');
          if (ok) setDocForm(null);
        }} />}
      {secForm && <SectionDialog section={secForm} count={docs.filter((d) => d.section_id === secForm.id).length}
        index={Math.max(0, sections.findIndex((s) => s.id === secForm.id))} onClose={() => setSecForm(null)}
        onSave={async (body) => {
          const ok = await run(() => (secForm.id ? axios.put(`${base}/sections/${secForm.id}`, body) : axios.post(`${base}/sections`, body)),
            secForm.id ? 'Section updated' : 'Section added');
          if (ok) setSecForm(null);
        }}
        onDelete={async () => { if (await run(() => axios.delete(`${base}/sections/${secForm.id}`), 'Section deleted')) setSecForm(null); }} />}
      {preview && <PreviewDialog doc={preview} onClose={() => setPreview(null)} onEmail={() => { setEmailDoc(preview); setPreview(null); }} />}
      {emailDoc && <EmailDialog doc={emailDoc} onClose={() => setEmailDoc(null)}
        onSend={async (body) => {
          try {
            const { data } = await axios.post(`${base}/docs/${emailDoc.id}/email`, body);
            const n = (data.results || []).length;
            if (data.sent === n) toast.success(`Sent to ${n} address${n !== 1 ? 'es' : ''}`);
            else toast.warning(`${data.sent} of ${n} sent — the rest were logged (check the website's mail setup)`);
            setEmailDoc(null);
          } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
        }} />}
    </div>
  );
}

function IconBtn({ title, onClick, href, danger, testid, children }) {
  const cls = `p-1.5 rounded-md [&>svg]:h-3.5 [&>svg]:w-3.5 ${danger ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-white'}`;
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" title={title} className={cls}>{children}</a>
    : <button type="button" title={title} onClick={onClick} className={cls} data-testid={testid}>{children}</button>;
}

function DocDialog({ doc, sections, onClose, onSave }) {
  const [f, setF] = useState({ title: doc.title || '', url: doc.url || '', section_id: doc.section_id || sections[0]?.id || '',
    description: doc.description || '', tags: (doc.tags || []).join(', ') });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Modal title={doc.id ? 'Edit document' : 'Add a document'} onClose={onClose}>
      <div className="space-y-2.5 text-sm">
        <label className="block space-y-1"><span className="text-xs text-slate-500">Link (Google Doc, Sheet, Slides, Drive file, PDF or any page)</span>
          <input className={field} value={f.url} onChange={set('url')} placeholder="https://docs.google.com/document/d/…" data-testid="library-doc-url" /></label>
        <label className="block space-y-1"><span className="text-xs text-slate-500">Title</span>
          <input className={field} value={f.title} onChange={set('title')} data-testid="library-doc-title" /></label>
        <label className="block space-y-1"><span className="text-xs text-slate-500">Section</span>
          <select className={field} value={f.section_id} onChange={set('section_id')}>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-slate-500">Description (optional)</span>
          <textarea className={field} rows={2} value={f.description} onChange={set('description')} /></label>
        <label className="block space-y-1"><span className="text-xs text-slate-500">Tags — comma separated (optional)</span>
          <input className={field} value={f.tags} onChange={set('tags')} placeholder="tryouts, basketball, forms" /></label>
        <p className="text-[11px] text-slate-500">The document must be shared (e.g. “Anyone in the organization with the link”) for Preview to show it.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Cancel</button>
          <button type="button" data-testid="library-doc-save"
            onClick={() => onSave({ ...f, tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean) })}
            className="rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm px-4 py-1.5">Save</button>
        </div>
      </div>
    </Modal>
  );
}

function SectionDialog({ section, index, count, onClose, onSave, onDelete }) {
  const [name, setName] = useState(section.name || '');
  const [color, setColor] = useState(section.color || PALETTE[index % PALETTE.length]);
  return (
    <Modal title={section.id ? 'Edit section' : 'Add a section'} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <label className="block space-y-1"><span className="text-xs text-slate-500">Name</span>
          <input className={field} value={name} onChange={(e) => setName(e.target.value)} data-testid="library-section-name" /></label>
        <div className="space-y-1"><span className="text-xs text-slate-500">Colour</span>
          <div className="flex flex-wrap gap-2">
            {PALETTE.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} title={c}
                className={`h-7 w-7 rounded-full ring-offset-2 dark:ring-offset-slate-900 ${color === c ? 'ring-2 ring-slate-900 dark:ring-white' : ''}`}
                style={{ background: c }} />
            ))}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-7 w-9 rounded cursor-pointer" title="Any colour" />
          </div>
        </div>
        <div className="rounded-lg px-3 py-2 text-white font-semibold" style={{ background: color }}>{name || 'Preview'}</div>
        <div className="flex items-center gap-2 pt-1">
          {section.id && (
            <button type="button" onClick={onDelete} disabled={count > 0}
              title={count ? 'Move or remove its documents first' : 'Delete this section'}
              className="text-xs text-red-600 hover:underline disabled:opacity-40 disabled:no-underline">Delete section{count ? ` (${count} docs)` : ''}</button>
          )}
          <span className="flex-1" />
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Cancel</button>
          <button type="button" onClick={() => onSave({ name, color })} data-testid="library-section-save"
            className="rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm px-4 py-1.5">Save</button>
        </div>
      </div>
    </Modal>
  );
}

function PreviewDialog({ doc, onClose, onEmail }) {
  const k = KIND[doc.kind] || KIND.link;
  const src = previewUrl(doc.url, doc.kind);
  return (
    <Modal title={doc.title} onClose={onClose} wide>
      <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
        <span className="rounded-full px-2 py-0.5 text-xs text-white" style={{ background: k.color }}>{k.label}</span>
        <span className="flex-1" />
        <a href={doc.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5">
          <ExternalLink className="h-4 w-4" /> Open</a>
        <a href={doc.url} target="_blank" rel="noopener noreferrer" title="Opens the document — then File ▸ Print (or Ctrl+P)"
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-slate-700 dark:text-slate-200">
          <Printer className="h-4 w-4" /> Print</a>
        <button type="button" onClick={onEmail}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-slate-700 dark:text-slate-200">
          <Mail className="h-4 w-4" /> Email</button>
      </div>
      <iframe title={`Preview of ${doc.title}`} src={src} className="w-full h-[70vh] rounded-lg border border-slate-200 dark:border-slate-700 bg-white"
        data-testid="library-preview-frame" />
      <p className="text-[11px] text-slate-500 mt-2">
        Blank or “request access”? The document isn't shared with you (or with the organization) — use Open, or share it in Google first.
      </p>
    </Modal>
  );
}

function EmailDialog({ doc, onClose, onSend }) {
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`Email “${doc.title}”`} onClose={onClose}>
      <div className="space-y-2.5 text-sm">
        <label className="block space-y-1"><span className="text-xs text-slate-500">To — one or more addresses, comma separated</span>
          <input className={field} value={to} onChange={(e) => setTo(e.target.value)} placeholder="coach@example.com, parent@example.com" data-testid="library-email-to" /></label>
        <label className="block space-y-1"><span className="text-xs text-slate-500">Message (optional)</span>
          <textarea className={field} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} /></label>
        <p className="text-[11px] text-slate-500">They receive the document's link with an Open button; replies come back to you. The recipient needs access to the document in Google.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-300">Cancel</button>
          <button type="button" disabled={busy || !to.trim()} data-testid="library-email-send"
            onClick={async () => { setBusy(true); await onSend({ to, message }); setBusy(false); }}
            className="inline-flex items-center gap-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm px-4 py-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send</button>
        </div>
      </div>
    </Modal>
  );
}
